import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  FileSearch, BadgeCheck, Banknote, Target,
  Plus, X, Award, Loader2, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

// Icon mapping for badges
const BADGE_ICONS = {
  'reviewer': FileSearch,
  'authorizer': BadgeCheck,
  'approver': Banknote,
  'marketing_manager': Target,
};

// Color mapping for badges
const BADGE_COLORS = {
  'blue': 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
  'green': 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
  'purple': 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200',
  'orange': 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200',
};

// Display badges inline (for employee cards/lists)
export const EmployeeBadges = ({ badges = [], size = 'sm', showTooltip = true }) => {
  if (!badges || badges.length === 0) return null;

  return (
    <TooltipProvider>
      <div className="flex flex-wrap gap-1">
        {badges.map((badge, idx) => {
          const IconComponent = BADGE_ICONS[badge.badge_type] || Award;
          const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS.blue;
          
          const badgeContent = (
            <div
              key={idx}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${colorClass}`}
            >
              <IconComponent className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />
              {size !== 'sm' && <span>{badge.name}</span>}
            </div>
          );

          if (showTooltip && size === 'sm') {
            return (
              <Tooltip key={idx}>
                <TooltipTrigger asChild>
                  {badgeContent}
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-semibold">{badge.name}</p>
                  <p className="text-xs text-gray-500">{badge.description}</p>
                </TooltipContent>
              </Tooltip>
            );
          }
          
          return badgeContent;
        })}
      </div>
    </TooltipProvider>
  );
};

// Badge management modal
const BadgeManagementModal = ({ 
  open, 
  onOpenChange, 
  employee, 
  onBadgeUpdate 
}) => {
  const { getAuthHeaders, currentOffice } = useAuth();
  const [availableBadges, setAvailableBadges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [notes, setNotes] = useState('');
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchBadgeDefinitions = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/employees/badges/definitions`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableBadges(data.badges || []);
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchBadgeDefinitions();
      setSelectedBadge(null);
      setNotes('');
    }
  }, [open]);

  const getEmployeeBadgeTypes = () => {
    return (employee?.special_badges || []).map(b => b.badge_type);
  };

  const handleAssignBadge = async (badgeType) => {
    setAssigning(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const response = await fetch(`${API_URL}/api/employees/${employee.id}/badges`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ badge_type: badgeType, notes }),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setSelectedBadge(null);
        setNotes('');
        onBadgeUpdate?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign badge');
      }
    } catch (error) {
      console.error('Error assigning badge:', error);
      toast.error('Failed to assign badge');
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveBadge = async (badgeType) => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const response = await fetch(`${API_URL}/api/employees/${employee.id}/badges/${badgeType}`, {
        method: 'DELETE',
        headers,
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        onBadgeUpdate?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove badge');
      }
    } catch (error) {
      console.error('Error removing badge:', error);
      toast.error('Failed to remove badge');
    }
  };

  const assignedBadgeTypes = getEmployeeBadgeTypes();
  const unassignedBadges = availableBadges.filter(b => !assignedBadgeTypes.includes(b.type));
  const assignedBadges = availableBadges.filter(b => assignedBadgeTypes.includes(b.type));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-amber-500" />
            Manage Special Badges
          </DialogTitle>
          <DialogDescription>
            {employee?.first_name} {employee?.last_name} • {employee?.employment?.job_title}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Currently Assigned Badges */}
            <div>
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                Assigned Badges ({assignedBadges.length})
              </Label>
              
              {assignedBadges.length === 0 ? (
                <p className="text-sm text-gray-500 mt-2 italic">No badges assigned yet</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {assignedBadges.map((badge) => {
                    const IconComponent = BADGE_ICONS[badge.type] || Award;
                    const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS.blue;
                    
                    return (
                      <div 
                        key={badge.type}
                        className={`flex items-center justify-between p-3 rounded-lg border ${colorClass}`}
                      >
                        <div className="flex items-center gap-3">
                          <IconComponent className="h-5 w-5" />
                          <div>
                            <p className="font-medium">{badge.name}</p>
                            <p className="text-xs opacity-75">{badge.description}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-100"
                          onClick={() => handleRemoveBadge(badge.type)}
                          data-testid={`remove-badge-${badge.type}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Available Badges to Assign */}
            <div>
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Plus className="h-4 w-4 text-green-500" />
                Available Badges ({unassignedBadges.length})
              </Label>
              
              {unassignedBadges.length === 0 ? (
                <p className="text-sm text-gray-500 mt-2 italic">All badges have been assigned</p>
              ) : (
                <ScrollArea className="h-48 mt-2">
                  <div className="space-y-2 pr-4">
                    {unassignedBadges.map((badge) => {
                      const IconComponent = BADGE_ICONS[badge.type] || Award;
                      const isSelected = selectedBadge === badge.type;
                      
                      return (
                        <div 
                          key={badge.type}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedBadge(isSelected ? null : badge.type)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${BADGE_COLORS[badge.color]?.split(' ')[0] || 'bg-gray-100'}`}>
                              <IconComponent className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{badge.name}</p>
                              <p className="text-xs text-gray-500">{badge.description}</p>
                            </div>
                            {!isSelected && (
                              <Plus className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                          
                          {isSelected && (
                            <div className="mt-3 pt-3 border-t space-y-3">
                              <div>
                                <Label className="text-xs">Notes (optional)</Label>
                                <Textarea
                                  placeholder="Add any notes about this badge assignment..."
                                  value={notes}
                                  onChange={(e) => setNotes(e.target.value)}
                                  rows={2}
                                  className="mt-1"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <Button
                                size="sm"
                                className="w-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssignBadge(badge.type);
                                }}
                                disabled={assigning}
                                data-testid={`assign-badge-${badge.type}`}
                              >
                                {assigning ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Assigning...
                                  </>
                                ) : (
                                  <>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Assign Badge
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BadgeManagementModal;
